// Sessão do cliente: `me` (GET /api/client/me) compartilhado por todas as páginas.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setUnauthorizedHandler, type Me } from './api';

type MeState = {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<Me | null>;
  setMe: (m: Me | null) => void;
  patch: (p: Partial<Me>) => void;
};

const MeContext = createContext<MeState | null>(null);

export function MeProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try { const m = await api<Me>('/api/client/me'); setMe(m); return m; }
    catch { setMe(null); return null; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { setUnauthorizedHandler(() => setMe(null)); return () => setUnauthorizedHandler(null); }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  // saldo e modelos da rede mudam sem a pessoa fazer nada
  useEffect(() => { if (!me) return; const id = setInterval(() => { void refresh(); }, 30000); return () => clearInterval(id); }, [me, refresh]);

  const value = useMemo<MeState>(() => ({
    me, loading, refresh, setMe,
    patch: (p) => setMe((cur) => (cur ? { ...cur, ...p } : cur)),
  }), [me, loading, refresh]);
  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): MeState {
  const v = useContext(MeContext);
  if (!v) throw new Error('useMe fora do MeProvider');
  return v;
}

/** Para páginas que só existem com sessão: `me` nunca é nulo aqui. */
export function useMeRequired(): MeState & { me: Me } {
  const v = useMe();
  if (!v.me) throw new Error('página exige sessão');
  return v as MeState & { me: Me };
}
